/**
 * Exclusion Manager Component
 *
 * Manages exclusion rules for AEO analysis.
 * Allows creating, editing, and removing rules to filter out
 * non-relevant URLs (CSS, JS, API endpoints, etc.)
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Filter, Plus, Trash2, Eye, EyeOff, AlertTriangle, Settings,
  FileCode, Lock, Unlock, ChevronLeft, ChevronRight, Search, Info
} from 'lucide-react';

import {
  getExclusionRules,
  addExclusionRule,
  removeExclusionRule,
  toggleExclusionRule,
  addManualExclusion,
  removeManualExclusion,
  getExcludedUrls,
  previewBulkExclusion,
  ExclusionRule,
  ManualExclusion,
  ExcludedUrl,
} from '@/services/aeoPrioritiesService';

interface Props {
  siteId: string;
  onRulesChanged?: () => void;
}

export function ExclusionManager({ siteId, onRulesChanged }: Props) {
  const [loading, setLoading] = useState(true);
  const [systemRules, setSystemRules] = useState<ExclusionRule[]>([]);
  const [siteRules, setSiteRules] = useState<ExclusionRule[]>([]);
  const [manualExclusions, setManualExclusions] = useState<ManualExclusion[]>([]);
  const [excludedUrls, setExcludedUrls] = useState<ExcludedUrl[]>([]);
  const [excludedTotal, setExcludedTotal] = useState(0);
  const [excludedPage, setExcludedPage] = useState(0);

  // New rule form
  const [showAddRule, setShowAddRule] = useState(false);
  const [newRuleType, setNewRuleType] = useState<string>('pattern');
  const [newRuleValue, setNewRuleValue] = useState('');
  const [newRuleReason, setNewRuleReason] = useState('');
  const [addingRule, setAddingRule] = useState(false);

  // Bulk preview
  const [previewPattern, setPreviewPattern] = useState('');
  const [previewType, setPreviewType] = useState<'pattern' | 'extension' | 'prefix'>('pattern');
  const [previewResults, setPreviewResults] = useState<ExcludedUrl[] | null>(null);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    loadData();
  }, [siteId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rulesRes, excludedRes] = await Promise.all([
        getExclusionRules(siteId),
        getExcludedUrls(siteId, { limit: 50, offset: 0 }),
      ]);
      setSystemRules(rulesRes.systemRules);
      setSiteRules(rulesRes.siteRules);
      setManualExclusions(rulesRes.manualExclusions);
      setExcludedUrls(excludedRes.urls);
      setExcludedTotal(excludedRes.total);
    } catch (err) {
      console.error('Failed to load exclusion data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = async () => {
    if (!newRuleValue.trim()) return;
    setAddingRule(true);
    try {
      await addExclusionRule(siteId, {
        ruleType: newRuleType,
        ruleValue: newRuleValue,
        reason: newRuleReason || undefined,
      });
      setNewRuleValue('');
      setNewRuleReason('');
      setShowAddRule(false);
      await loadData();
      onRulesChanged?.();
    } catch (err) {
      console.error('Failed to add rule:', err);
    } finally {
      setAddingRule(false);
    }
  };

  const handleRemoveRule = async (ruleId: string) => {
    try {
      await removeExclusionRule(siteId, ruleId);
      await loadData();
      onRulesChanged?.();
    } catch (err) {
      console.error('Failed to remove rule:', err);
    }
  };

  const handleToggleRule = async (ruleId: string, isActive: boolean) => {
    try {
      await toggleExclusionRule(siteId, ruleId, isActive);
      await loadData();
      onRulesChanged?.();
    } catch (err) {
      console.error('Failed to toggle rule:', err);
    }
  };

  const handleRemoveManualExclusion = async (urlPath: string) => {
    try {
      await removeManualExclusion(siteId, urlPath);
      await loadData();
      onRulesChanged?.();
    } catch (err) {
      console.error('Failed to remove exclusion:', err);
    }
  };

  const handlePreview = async () => {
    if (!previewPattern.trim()) return;
    setPreviewing(true);
    try {
      const result = await previewBulkExclusion(siteId, previewPattern, previewType);
      setPreviewResults(result.preview);
      setPreviewTotal(result.totalAffected);
    } catch (err) {
      console.error('Failed to preview:', err);
    } finally {
      setPreviewing(false);
    }
  };

  const handleApplyPreviewAsRule = async () => {
    if (!previewPattern.trim()) return;
    setAddingRule(true);
    try {
      await addExclusionRule(siteId, {
        ruleType: previewType,
        ruleValue: previewPattern,
        reason: `Bulk exclusion: ${previewTotal} URLs`,
      });
      setPreviewPattern('');
      setPreviewResults(null);
      await loadData();
      onRulesChanged?.();
    } catch (err) {
      console.error('Failed to add rule:', err);
    } finally {
      setAddingRule(false);
    }
  };

  const getRuleTypeBadge = (type: string) => {
    switch (type) {
      case 'extension':
        return <Badge variant="secondary">Extension</Badge>;
      case 'prefix':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-700">Prefix</Badge>;
      case 'pattern':
        return <Badge variant="secondary" className="bg-purple-100 text-purple-700">Pattern</Badge>;
      case 'exact':
        return <Badge variant="secondary" className="bg-orange-100 text-orange-700">Exact</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Exclusion Manager
              </CardTitle>
              <CardDescription className="mt-1">
                Filter out non-relevant URLs (CSS, JS, API endpoints) to focus analysis on products and categories.
              </CardDescription>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="text-muted-foreground">
                <span className="font-semibold text-foreground">{excludedTotal.toLocaleString()}</span> URLs excluded
              </div>
              <div className="text-muted-foreground">
                <span className="font-semibold text-foreground">{systemRules.length + siteRules.length}</span> rules active
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue="rules">
        <TabsList>
          <TabsTrigger value="rules">Exclusion Rules</TabsTrigger>
          <TabsTrigger value="excluded">Excluded URLs</TabsTrigger>
          <TabsTrigger value="bulk">Bulk Exclusion</TabsTrigger>
        </TabsList>

        {/* Exclusion Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          {/* System Rules */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  System Rules
                </CardTitle>
                <Badge variant="outline">{systemRules.length} rules</Badge>
              </div>
              <CardDescription className="text-xs">
                Default rules that filter common non-content URLs. Can be disabled but not deleted.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Type</TableHead>
                      <TableHead>Pattern</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="w-[80px] text-center">Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {systemRules.map(rule => (
                      <TableRow key={rule.id}>
                        <TableCell>{getRuleTypeBadge(rule.rule_type)}</TableCell>
                        <TableCell className="font-mono text-sm">{rule.rule_value}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{rule.reason}</TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={rule.is_active}
                            onCheckedChange={(checked) => handleToggleRule(rule.id, checked)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Site Rules */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Unlock className="h-4 w-4" />
                  Custom Rules
                </CardTitle>
                <Dialog open={showAddRule} onOpenChange={setShowAddRule}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Rule
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Exclusion Rule</DialogTitle>
                      <DialogDescription>
                        Create a new rule to exclude URLs from AEO analysis.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Rule Type</label>
                        <Select value={newRuleType} onValueChange={setNewRuleType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="extension">Extension (e.g., .css, .js)</SelectItem>
                            <SelectItem value="prefix">Prefix (e.g., /api/, /admin/)</SelectItem>
                            <SelectItem value="pattern">Pattern (e.g., %/checkout/%)</SelectItem>
                            <SelectItem value="exact">Exact Match</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Value</label>
                        <Input
                          placeholder={
                            newRuleType === 'extension' ? '.css' :
                            newRuleType === 'prefix' ? '/api/' :
                            newRuleType === 'pattern' ? '%/checkout/%' :
                            '/exact/path'
                          }
                          value={newRuleValue}
                          onChange={(e) => setNewRuleValue(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          {newRuleType === 'pattern' && 'Use % as wildcard (e.g., %/cart/% matches any URL containing /cart/)'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Reason (optional)</label>
                        <Input
                          placeholder="Why exclude these URLs?"
                          value={newRuleReason}
                          onChange={(e) => setNewRuleReason(e.target.value)}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddRule(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleAddRule} disabled={!newRuleValue.trim() || addingRule}>
                        {addingRule ? 'Adding...' : 'Add Rule'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <CardDescription className="text-xs">
                Site-specific rules you've created. These can be edited or deleted.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {siteRules.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Type</TableHead>
                      <TableHead>Pattern</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="w-[80px] text-center">Active</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {siteRules.map(rule => (
                      <TableRow key={rule.id}>
                        <TableCell>{getRuleTypeBadge(rule.rule_type)}</TableCell>
                        <TableCell className="font-mono text-sm">{rule.rule_value}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{rule.reason || '-'}</TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={rule.is_active}
                            onCheckedChange={(checked) => handleToggleRule(rule.id, checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveRule(rule.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Filter className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No custom rules yet.</p>
                  <p className="text-sm">Click "Add Rule" to create site-specific exclusions.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Manual Exclusions */}
          {manualExclusions.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <EyeOff className="h-4 w-4" />
                  Manual Exclusions
                </CardTitle>
                <CardDescription className="text-xs">
                  Individual URLs excluded manually from the priorities tables.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>URL</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {manualExclusions.map(exc => (
                        <TableRow key={exc.id}>
                          <TableCell className="font-mono text-sm truncate max-w-[400px]">
                            {exc.url_path}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {exc.reason || '-'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleRemoveManualExclusion(exc.url_path)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Excluded URLs Tab */}
        <TabsContent value="excluded">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Excluded URLs</CardTitle>
              <CardDescription>
                URLs currently excluded from AEO analysis ({excludedTotal.toLocaleString()} total)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>Exclusion Reason</TableHead>
                    <TableHead className="text-right">Bot Visits</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {excludedUrls.map(url => (
                    <TableRow key={url.url_path}>
                      <TableCell className="font-mono text-sm truncate max-w-[400px]">
                        {url.url_path}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {url.exclusion_reason}
                      </TableCell>
                      <TableCell className="text-right">
                        {url.bot_visits_30d.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {excludedUrls.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                        No excluded URLs found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {excludedTotal > 50 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {excludedPage * 50 + 1} - {Math.min((excludedPage + 1) * 50, excludedTotal)} of {excludedTotal}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExcludedPage(p => Math.max(0, p - 1))}
                      disabled={excludedPage === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const newPage = excludedPage + 1;
                        const res = await getExcludedUrls(siteId, { limit: 50, offset: newPage * 50 });
                        setExcludedUrls(res.urls);
                        setExcludedPage(newPage);
                      }}
                      disabled={(excludedPage + 1) * 50 >= excludedTotal}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bulk Exclusion Tab */}
        <TabsContent value="bulk">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Bulk Exclusion Preview</CardTitle>
              <CardDescription>
                Test a pattern to see which URLs would be excluded before creating a rule.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Select value={previewType} onValueChange={(v) => setPreviewType(v as any)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="extension">Extension</SelectItem>
                    <SelectItem value="prefix">Prefix</SelectItem>
                    <SelectItem value="pattern">Pattern</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder={
                    previewType === 'extension' ? '.woff2' :
                    previewType === 'prefix' ? '/static/' :
                    '%/tracking/%'
                  }
                  value={previewPattern}
                  onChange={(e) => setPreviewPattern(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handlePreview} disabled={!previewPattern.trim() || previewing}>
                  <Search className="h-4 w-4 mr-2" />
                  {previewing ? 'Searching...' : 'Preview'}
                </Button>
              </div>

              {previewResults !== null && (
                <div className="space-y-4">
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      This pattern would exclude <strong>{previewTotal.toLocaleString()}</strong> URLs from analysis.
                    </AlertDescription>
                  </Alert>

                  {previewResults.length > 0 && (
                    <>
                      <div className="max-h-64 overflow-y-auto border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Sample URLs (first 100)</TableHead>
                              <TableHead className="text-right w-[100px]">Bot Visits</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {previewResults.slice(0, 20).map((url, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-mono text-xs truncate max-w-[500px]">
                                  {url.url_path}
                                </TableCell>
                                <TableCell className="text-right">
                                  {url.bot_visits_30d.toLocaleString()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      <div className="flex justify-end">
                        <Button onClick={handleApplyPreviewAsRule} disabled={addingRule}>
                          <Plus className="h-4 w-4 mr-2" />
                          {addingRule ? 'Creating Rule...' : 'Create Exclusion Rule'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
